import React, { useState, useEffect, useCallback, useMemo } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import Card from "react-bootstrap/Card";
import Dropdown from "react-bootstrap/Dropdown";
import { useTranslation } from "react-i18next";
import RosaryIcon from "./RosaryIcon";
import { useTVNavigation } from "./hooks/useTVNavigation";
import "./Rosario.scss";

const Rosario = ({ isTV = false }) => {
  const { t } = useTranslation();

  // Hook de navegación para TV
  useTVNavigation(isTV);

  // Estado: phase = 'rosary' | 'completed' | 'litany' | 'all_done'
  const [phase, setPhase] = useState('rosary');
  const [step, setStep] = useState(0);
  const [mysteryType, setMysteryType] = useState(null);
  const [showMenu, setShowMenu] = useState(false);

  // Estructura de las Letanías Lauretanas - envuelto en useMemo
  const litaniesStructure = useMemo(() => [
    { type: "lord_mercy", repeat: 3 },
    { type: "holy_mary" },
    { type: "mother_christ", repeat: 3 },
    { type: "lord_mercy_short", repeat: 3 },
    { type: "virgin_invocations" },
    { type: "lord_mercy_short", repeat: 3 },
    { type: "more_invocations" },
    { type: "lord_mercy_short", repeat: 3 },
    { type: "final_invocations" },
    { type: "final_prayer" }
  ], []);

  const virginInvocations = useMemo(() => [
    "mother_of_christ", "mother_of_divine_grace", "mother_most_pure",
    "mother_most_chaste", "mother_ever_virgin", "mother_immaculate",
    "mother_of_the_church", "mother_of_mercy", "virgin_most_prudent",
    "virgin_most_venerable", "virgin_most_praiseworthy", "virgin_most_powerful",
    "virgin_most_clement", "virgin_most_faithful", "mirror_of_justice",
    "seat_of_wisdom", "cause_of_our_joy", "vessel_of_honor",
    "singular_vessel_of_devotion", "mystical_rose", "tower_of_david",
    "tower_of_ivory", "house_of_gold", "ark_of_the_covenant",
    "gate_of_heaven", "morning_star", "health_of_the_sick",
    "refuge_of_sinners", "comforter_of_the_afflicted", "help_of_christians",
    "queen_of_angels", "queen_of_patriarchs", "queen_of_prophets",
    "queen_of_apostles", "queen_of_martyrs", "queen_of_confessors",
    "queen_of_virgins", "queen_of_all_saints", "queen_conceived_without_sin",
    "queen_assumed_into_heaven", "queen_of_the_most_holy_rosary"
  ], []);

  const moreInvocations = useMemo(() => [
    "queen_of_the_most_holy_rosary", "mother_of_the_church", "mother_of_mercy",
    "mirror_of_justice", "seat_of_wisdom", "cause_of_our_joy",
    "vessel_of_honor", "singular_vessel_of_devotion", "mystical_rose"
  ], []);

  const finalInvocations = useMemo(() => [
    "queen_of_angels", "queen_of_patriarchs", "queen_of_prophets",
    "queen_of_apostles", "queen_of_martyrs", "queen_of_confessors",
    "queen_of_virgins", "queen_of_all_saints", "queen_conceived_without_sin",
    "queen_assumed_into_heaven", "queen_of_the_most_holy_rosary",
    "mother_of_the_church", "mother_of_mercy", "mirror_of_justice",
    "seat_of_wisdom", "cause_of_our_joy"
  ], []);

  const totalRosarySteps = 60;
  const totalLitanySteps = 54;

  // Detectar misterio del día automáticamente
  useEffect(() => {
    if (!mysteryType) {
      const day = new Date().getDay();
      const dayMysteries = {
        0: "glorious", 1: "joyful", 2: "sorrowful",
        3: "glorious", 4: "luminous", 5: "sorrowful", 6: "joyful",
      };
      setMysteryType(dayMysteries[day]);
    }
  }, [mysteryType]);

  const currentDecade = Math.floor(step / 12);

  const getPrayerType = useCallback(() => {
    if (step === 0) return "intro";
    const pos = (step - 1) % 12;
    if (pos === 0) return "our_father";
    if (pos >= 1 && pos <= 10) return "hail_mary";
    return "glory_be";
  }, [step]);

  const getCurrentMystery = useCallback(() => {
    if (!mysteryType || step >= totalRosarySteps) return null;
    try {
      const mysteries = t(`rosary.mysteries.${mysteryType}`, { returnObjects: true });
      return mysteries[currentDecade] || null;
    } catch {
      return null;
    }
  }, [mysteryType, currentDecade, step, t]);

  const currentMystery = getCurrentMystery();
  const prayerType = getPrayerType();

  const getLitanyInfo = useCallback(() => {
    if (phase !== 'litany') return null;

    let totalCount = 0;
    for (const section of litaniesStructure) {
      if (section.type === "virgin_invocations") {
        for (let i = 0; i < virginInvocations.length; i++) {
          totalCount++;
          if (totalCount === step) {
            return {
              title: t(`litany.${virginInvocations[i]}`),
              response: t("litany.pray_for_us"),
              isMary: true
            };
          }
        }
      } else if (section.type === "more_invocations") {
        for (let i = 0; i < moreInvocations.length; i++) {
          totalCount++;
          if (totalCount === step) {
            return {
              title: t(`litany.${moreInvocations[i]}`),
              response: t("litany.pray_for_us"),
              isMary: true
            };
          }
        }
      } else if (section.type === "final_invocations") {
        for (let i = 0; i < finalInvocations.length; i++) {
          totalCount++;
          if (totalCount === step) {
            return {
              title: t(`litany.${finalInvocations[i]}`),
              response: t("litany.pray_for_us"),
              isMary: true
            };
          }
        }
      } else if (section.type === "holy_mary") {
        totalCount++;
        if (totalCount === step) {
          return {
            title: t("litany.holy_mary"),
            response: t("litany.pray_for_us"),
            isMary: true
          };
        }
      } else if (section.type === "final_prayer") {
        totalCount++;
        if (totalCount === step) {
          return {
            title: t("litany.final_prayer"),
            isFinal: true
          };
        }
      } else if (section.type === "lord_mercy" || section.type === "lord_mercy_short") {
        for (let i = 0; i < section.repeat; i++) {
          totalCount++;
          if (totalCount === step) {
            return {
              title: t("litany.lord_have_mercy"),
              response: t("litany.lord_have_mercy_response"),
              isLord: true
            };
          }
        }
      } else if (section.type === "mother_christ") {
        totalCount++;
        if (totalCount === step) {
          return {
            title: t("litany.mother_of_christ"),
            response: t("litany.pray_for_us"),
            isMary: true
          };
        }
      }
    }
    return null;
  }, [step, phase, t, virginInvocations, moreInvocations, finalInvocations, litaniesStructure]);

  const currentLitany = getLitanyInfo();

  // Handlers
  const handleNext = () => {
    if (phase === 'rosary') {
      if (step < totalRosarySteps) {
        setStep(step + 1);
      } else {
        setPhase('completed');
      }
    } else if (phase === 'litany') {
      if (step < totalLitanySteps) {
        setStep(step + 1);
      } else {
        setPhase('all_done');
      }
    }
  };

  const handlePrevious = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const handleReset = () => {
    setStep(0);
    setPhase('rosary');
  };

  const handleStartLitany = () => {
    setPhase('litany');
    setStep(0);
  };

  const handleFinish = () => {
    setPhase('all_done');
  };

  const changeMysteryType = (type) => {
    setMysteryType(type);
    setStep(0);
    setShowMenu(false);
  };

  // Función auxiliar para manejar eventos de teclado en modo TV
  const handleKeyPress = (callback) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback(e);
    }
  };

  // Props para elementos interactivos en modo TV
  const tvInteractiveProps = isTV ? {
    tabIndex: 0,
    role: 'button'
  } : {};

  // ===== VISTA DE INICIO DEL ROSARIO =====
  if (step === 0 && phase === 'rosary') {
    return (
      <div className="rosario-simple">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="mb-4">
                <div className="rosary-icon">
                  <RosaryIcon width={100} height={150} />
                </div>
                <h2 className="mb-3">{t("rosary.title")}</h2>
                <p className="text-muted">
                  {t("rosary.simple.subtitle")}
                </p>
              </div>

              <Card className="text-center mystery-preview mb-4">
                <Card.Body>
                  <div className="mystery-icon-large">
                    {getMysteryIcon(mysteryType)}
                  </div>
                  <h3 className="mt-3">
                    {t(`rosary.mystery_types.${mysteryType}`)}
                  </h3>
                  <p className="text-muted">
                    {getMysteryDay(mysteryType)}
                  </p>
                </Card.Body>
              </Card>

              <Dropdown show={showMenu} onToggle={setShowMenu} className="mb-4">
                <Dropdown.Toggle variant="outline-secondary" size="lg" {...tvInteractiveProps}>
                  {t("rosary.simple.change_mystery")}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {["joyful", "luminous", "sorrowful", "glorious"].map((type) => (
                    <Dropdown.Item
                      key={type}
                      active={type === mysteryType}
                      onClick={() => changeMysteryType(type)}
                      {...tvInteractiveProps}
                      onKeyDown={handleKeyPress(() => changeMysteryType(type))}
                    >
                      {getMysteryIcon(type)} {t(`rosary.mystery_types.${type}`)}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <Button
                variant="primary"
                size="lg"
                className="w-100 py-3 start-button"
                onClick={handleNext}
                {...tvInteractiveProps}
                onKeyDown={handleKeyPress(handleNext)}
              >
                {t("rosary.simple.start")} →
              </Button>

              <p className="text-muted mt-4">
                <small>📖 {t("rosary.prayers.sign_of_cross")}</small>
              </p>

              <Card className="instructions-card mb-4">
                <Card.Body>
                  <p className="mb-0">{t("rosary.instructions")}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE ROSARIO COMPLETADO - OFRECER LETANÍAS =====
  if (phase === 'completed') {
    return (
      <div className="rosario-simple completion">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="completion-icon">✝️</div>
              <h1 className="mt-4">{t("rosary.completed.title")}</h1>
              <p className="lead">{t("rosary.completed.message")}</p>
              <p className="text-muted mb-4">{t("rosary.completed.blessing")}</p>

              <Card className="completion-prayer mb-4">
                <Card.Body>
                  <p>{t("rosary.prayers.holy_queen")}</p>
                </Card.Body>
              </Card>

              <p className="mb-3">{t("litany.after_rosary.message")}</p>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleStartLitany}
                  {...tvInteractiveProps}
                  onKeyDown={handleKeyPress(handleStartLitany)}
                >
                  {t("litany.after_rosary.pray_litany")} 🙏
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={handleFinish}
                  {...tvInteractiveProps}
                  onKeyDown={handleKeyPress(handleFinish)}
                >
                  {t("litany.after_rosary.finish")}
                </Button>
                <Button
                  variant="link"
                  href="/"
                  {...tvInteractiveProps}
                >
                  {t("rosary.actions.home")}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE INICIO DE LETANÍAS =====
  if (step === 0 && phase === 'litany') {
    return (
      <div className="rosario-simple">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <Button
                variant="link"
                className="text-muted mb-3"
                onClick={handleReset}
                {...tvInteractiveProps}
                onKeyDown={handleKeyPress(handleReset)}
              >
                ← {t("rosary.actions.back")}
              </Button>

              <div className="mb-5">
                <div className="rosary-icon">🙏</div>
                <h1 className="mb-3">{t("litany.title")}</h1>
                <p className="text-muted lead">{t("litany.subtitle")}</p>
              </div>

              <Card className="text-center mystery-preview mb-4">
                <Card.Body>
                  <div className="mystery-icon-large">🙏</div>
                  <h3 className="mt-3">{t("litany.holy_mary")}</h3>
                  <p className="text-muted mt-3">
                    {t("litany.description")}
                  </p>
                </Card.Body>
              </Card>

              <div className="text-center mb-4">
                <p className="text-muted">
                  <small>📖 {t("litany.origin")}</small>
                </p>
              </div>

              <Card className="instructions-card mb-4">
                <Card.Body>
                  <p className="mb-0">{t("litany.instructions")}</p>
                </Card.Body>
              </Card>

              <Button
                variant="primary"
                size="lg"
                className="w-100 py-3 start-button"
                onClick={handleNext}
                {...tvInteractiveProps}
                onKeyDown={handleKeyPress(handleNext)}
              >
                {t("coronilla.start")} →
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE TODO COMPLETADO =====
  if (phase === 'all_done') {
    return (
      <div className="rosario-simple completion">
        <Container className="rosario-container py-4">
          <Row className="justify-content-center">
            <Col md={8} lg={6} className="text-center">
              <div className="completion-icon">🙏</div>
              <h1 className="mt-4">{t("litany.completed.title")}</h1>
              <p className="lead">{t("litany.completed.message")}</p>
              <p className="text-muted mb-4">{t("litany.completed.blessing")}</p>

              <Card className="completion-prayer mb-4">
                <Card.Body>
                  <p>{t("litany.final_prayer")}</p>
                </Card.Body>
              </Card>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleReset}
                  {...tvInteractiveProps}
                  onKeyDown={handleKeyPress(handleReset)}
                >
                  {t("litany.actions.pray_again")}
                </Button>
                <Button
                  variant="outline-secondary"
                  href="/"
                  {...tvInteractiveProps}
                >
                  {t("rosary.actions.home")}
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE ORACIÓN DEL ROSARIO =====
  if (phase === 'rosary') {
    return (
      <div className="rosario-simple prayer-view">
        <Container className="rosario-container">
          <div className="prayer-content-wrapper">
            <div className="rosario-header mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Button
                  variant="link"
                  className="text-muted p-0"
                  onClick={handleReset}
                  {...tvInteractiveProps}
                  onKeyDown={handleKeyPress(handleReset)}
                >
                  ← {t("rosary.actions.back")}
                </Button>
                <div className="decade-counter">
                  <span className="decade-current">{currentDecade + 1}</span>
                  <span className="decade-separator">/</span>
                  <span className="decade-total">5</span>
                </div>
              </div>
              <div className="mystery-label text-center">
                {t(`rosary.mystery_types.${mysteryType}`)}
              </div>
            </div>

            <div className="decade-progress-container mb-4">
              {Array.from({ length: 5 }, (_, i) => {
                const decadeProgress = i < currentDecade ? 100 : i > currentDecade ? 0 : ((step - 1) % 12) / 12 * 100;
                return (
                  <div
                    key={i}
                    className={`decade-segment ${i < currentDecade ? "completed" : ""} ${
                      i === currentDecade ? "active" : ""
                    }`}
                  >
                    <div className="decade-segment-fill" style={{ width: `${decadeProgress}%` }} />
                    <span className="decade-number">{i + 1}</span>
                  </div>
                );
              })}
            </div>

            <Card className="prayer-card-main" onClick={(e) => { if (!e.target.closest('button')) handleNext(); }}>
              <Card.Body className="prayer-card-body">
                <div className="prayer-content">
                  <h2 className="prayer-title-main mb-4">
                    {t(`rosary.bead.${prayerType}`)}
                  </h2>
                  <p className="prayer-text-main lead">
                    {t(`rosary.prayers.${prayerType}`)}
                  </p>
                  {currentMystery && (
                    <div className="current-mystery-compact mt-4 pt-3 border-top">
                      <p className="mb-1">
                        <strong>{currentMystery.title}</strong>
                      </p>
                      <p className="text-muted small mb-1">
                        {t("rosary.labels.fruit")}: {currentMystery.fruit}
                      </p>
                      {currentMystery.scripture && (
                        <p className="mystery-scripture small mb-0">
                          📖 {currentMystery.scripture}
                        </p>
                      )}
                    </div>
                  )}
                  <div className="tap-indicator mt-4 text-muted">
                    <small>{t("rosary.simple.tap_continue")}</small>
                  </div>
                </div>
              </Card.Body>
            </Card>

            <div className="navigation-controls d-flex justify-content-between gap-2 mt-4">
              <Button
                variant="outline-secondary"
                className="nav-btn flex-grow-1"
                onClick={handlePrevious}
                disabled={step === 1}
                {...tvInteractiveProps}
                onKeyDown={handleKeyPress(handlePrevious)}
              >
                ← {t("rosary.simple.previous")}
              </Button>
              <Button
                variant="primary"
                className="nav-btn flex-grow-1"
                onClick={(e) => { e.stopPropagation(); handleNext(); }}
                {...tvInteractiveProps}
                onKeyDown={handleKeyPress((e) => { e.stopPropagation(); handleNext(); })}
              >
                {t("rosary.simple.next")} →
              </Button>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  // ===== VISTA DE LETANÍAS =====
  const progress = Math.min((step / totalLitanySteps) * 100, 100);

  return (
    <div className="rosario-simple prayer-view">
      <Container className="rosario-container">
        <div className="prayer-content-wrapper">
          <div className="rosario-header mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Button
                variant="link"
                className="text-muted p-0"
                onClick={() => setPhase('completed')}
                {...tvInteractiveProps}
                onKeyDown={handleKeyPress(() => setPhase('completed'))}
              >
                ← {t("rosary.actions.back")}
              </Button>
              <div className="decade-counter">
                <span className="decade-current">{step}</span>
                <span className="decade-separator">/</span>
                <span className="decade-total">{totalLitanySteps + 1}</span>
              </div>
            </div>
            <div className="mystery-label text-center">
              {t("litany.title")}
            </div>
          </div>

          <div className="decade-progress-container mb-4">
            <div className="decade-segment active" style={{ width: `${progress}%` }}>
              <div className="decade-segment-fill" style={{ width: `${progress}%` }} />
            </div>
          </div>

          <Card className="prayer-card-main" onClick={(e) => { if (!e.target.closest('button')) handleNext(); }}>
            <Card.Body className="prayer-card-body">
              <div className="prayer-content">
                {currentLitany?.isFinal ? (
                  <>
                    <h2 className="prayer-title-main mb-4">
                      {t("rosary.prayers.holy_queen")}
                    </h2>
                    <p className="prayer-text-main lead" dangerouslySetInnerHTML={{ __html: currentLitany.title }} />
                  </>
                ) : (
                  <>
                    <h2 className="prayer-title-main mb-4">
                      {currentLitany?.isLord ? t("litany.christ_have_mercy") : t("litany.holy_mary")}
                    </h2>
                    <p className="prayer-text-main lead mb-3">
                      {currentLitany?.title}
                    </p>
                    <p className="prayer-response lead text-muted">
                      <strong>{currentLitany?.response}</strong>
                    </p>
                    <div className="tap-indicator mt-4 text-muted">
                      <small>{t("rosary.simple.tap_continue")}</small>
                    </div>
                  </>
                )}
              </div>
            </Card.Body>
          </Card>

          <div className="navigation-controls d-flex justify-content-between gap-2 mt-4">
            <Button
              variant="outline-secondary"
              className="nav-btn flex-grow-1"
              onClick={handlePrevious}
              disabled={step === 1}
              {...tvInteractiveProps}
              onKeyDown={handleKeyPress(handlePrevious)}
            >
              ← {t("rosary.simple.previous")}
            </Button>
            <Button
              variant="primary"
              className="nav-btn flex-grow-1"
              onClick={(e) => { e.stopPropagation(); handleNext(); }}
              {...tvInteractiveProps}
              onKeyDown={handleKeyPress((e) => { e.stopPropagation(); handleNext(); })}
            >
              {t("rosary.simple.next")} →
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
};

// Helper functions
const getMysteryIcon = (type) => {
  const icons = {
    joyful: "🕊️",
    luminous: "💡",
    sorrowful: "❤️",
    glorious: "👑",
  };
  return icons[type] || "";
};

const getMysteryDay = (type) => {
  const days = {
    joyful: "Lunes y Sábado",
    luminous: "Jueves",
    sorrowful: "Martes y Viernes",
    glorious: "Miércoles y Domingo",
  };
  return days[type] || "";
};

export default Rosario;
